import { styled, type SxProps, type Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

import { SIZES } from '../../assets/constants/constants';
import { HERO_VARIANT } from '../../hooks/GetHero';

export type SectionBackground =
  | 'default'
  | 'contrast'
  | 'paper'
  | 'primary'
  | 'secondary'
  | 'light-gray'
  | `gradient-${typeof HERO_VARIANT.BLUE}`
  | `gradient-${typeof HERO_VARIANT.RED}`
  | `gradient-${typeof HERO_VARIANT.YELLOW}`;

type StyledSectionProps = {
  fullWidth?: boolean;
  overflow?: 'visible' | 'hidden';
  last?: boolean;
  fullHeight?: boolean;
  extraPadding?: boolean;
  background?: SectionBackground;
};

const StyledSection = styled('section')<StyledSectionProps>(
  ({
    theme,
    fullWidth,
    overflow,
    last,
    fullHeight,
    extraPadding,
    background,
  }) => ({
    position: 'relative',
    overflow: overflow,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',

    '& > *': {
      maxWidth: fullWidth ? undefined : SIZES.layout.width.md,
      zIndex: overflow === 'visible' ? 1050 : 2,
    },

    ...(background === 'contrast' && {
      backgroundColor: theme.palette.background.contrast,
    }),
    ...(background === 'default' && {
      backgroundColor: theme.palette.background.default,
    }),
    ...(background === 'paper' && {
      backgroundColor: theme.palette.background.paper,
    }),
    ...(background === 'primary' && {
      backgroundColor: theme.palette.primary.main,
    }),
    ...(background === 'secondary' && {
      backgroundColor: theme.palette.secondary.main,
    }),
    ...(background === 'light-gray' && {
      backgroundColor: theme.palette.background.lightest,
    }),
    ...(background === `gradient-${HERO_VARIANT.BLUE}` && {
      background: `radial-gradient(98.73% 52.1% at 47.33% 51.23%, rgba(255, 255, 255, 0.49) 0%, rgba(255, 255, 255, 0) 97.5%), ${theme.palette.primary.main}`,
      [theme.breakpoints.up('md')]: {
        background: `radial-gradient(61.37% 762.47% at 80.21% 50%, rgba(255, 255, 255, 0.46) 0%, rgba(255, 255, 255, 0.01) 67.16%, rgba(255, 255, 255, 0.01) 100%),  ${theme.palette.primary.main}`,
      },
    }),
    ...(background === `gradient-${HERO_VARIANT.YELLOW}` && {
      background: `radial-gradient(98.73% 52.1% at 47.33% 51.23%, rgba(255, 255, 255, 0.49) 0%, rgba(255, 255, 255, 0) 97.5%), ${theme.palette.secondary.main}`,
      [theme.breakpoints.up('md')]: {
        background: `radial-gradient(61.37% 762.47% at 80.21% 50%, rgba(255, 255, 255, 0.46) 0%, rgba(255, 255, 255, 0.01) 67.16%, rgba(255, 255, 255, 0.01) 100%), ${theme.palette.secondary.main}`,
      },
    }),
    ...(background === `gradient-${HERO_VARIANT.RED}` && {
      background: `radial-gradient(98.73% 52.1% at 47.33% 51.23%, rgba(255, 255, 255, 0.49) 0%, rgba(255, 255, 255, 0) 97.5%), ${theme.palette.accent.main}`,
      [theme.breakpoints.up('md')]: {
        background: `radial-gradient(61.37% 762.47% at 80.21% 50%, rgba(255, 255, 255, 0.46) 0%, rgba(255, 255, 255, 0.01) 67.16%, rgba(255, 255, 255, 0.01) 100%), ${theme.palette.accent.main}`,
      },
    }),

    padding: theme.spacing(
      fullHeight ? 0 : extraPadding ? 10 : 4,
      fullWidth ? 0 : 4,
      fullHeight ? 0 : last ? 18 : extraPadding ? 10 : 4,
    ),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(
        fullHeight ? 0 : extraPadding ? 12 : 6,
        fullWidth ? 0 : 4,
        fullHeight ? 0 : last ? 22 : extraPadding ? 12 : 6,
      ),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(
        fullHeight ? 0 : extraPadding ? 14 : 8,
        fullWidth ? 0 : 10,
        fullHeight ? 0 : last ? 25 : extraPadding ? 18 : 8,
      ),
    },
    [theme.breakpoints.up('lg')]: {
      paddingBottom: theme.spacing(
        fullHeight ? 0 : last ? 12 : extraPadding ? 18 : 8,
      ),
    },
  }),
);

export type SectionProps = StyledSectionProps & {
  children?: ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
};

const Section: React.FC<SectionProps> = ({
  children,
  background = 'default',
  overflow = 'hidden',
  fullWidth,
  fullHeight,
  last,
  extraPadding,
  className,
  ...props
}) => {
  if (!children) return null;
  return (
    <StyledSection
      background={background}
      overflow={overflow}
      fullWidth={fullWidth}
      fullHeight={fullHeight}
      last={last}
      extraPadding={extraPadding}
      className={className}
      {...props}
    >
      {children}
    </StyledSection>
  );
};

export default Section;
